import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from '../../lib/icons';

const norm = (v) => String(v ?? '').toLowerCase().replace(/[\s.\-/]/g, '');

const champs = (c) => [c.nom, c.code, c.tel, c.mf, c.cin, c.cnss, c.region, c.codeClient, c.codeFournisseur];

// Règle de correspondance partagée par le combobox et par le champ de recherche seul.
export const correspondContact = (c, q) => {
  const t = norm(q);
  return t ? champs(c).some((v) => norm(v).includes(t)) : true;
};

// Champ de recherche autonome : même habillage que le combobox, mais il filtre une
// liste déjà affichée au lieu de sélectionner un contact.
export const RechercheContacts = ({
  valeur,
  onChange,
  testid,
  placeholder = 'Rechercher : nom, code client / fournisseur, téléphone…',
  indication,
  className = '',
}) => (
  <div
    className={`flex h-11 items-center gap-2 border border-slate-300 bg-slate-50 px-3 transition-colors duration-150 focus-within:border-slate-900 focus-within:bg-white sm:h-10 ${className}`}
  >
    <Search size={14} className="flex-shrink-0 text-slate-400" />
    <input
      data-testid={testid}
      value={valeur}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="h-full w-full min-w-0 bg-transparent font-data text-sm text-slate-900 outline-none placeholder:text-slate-400"
    />
    {indication && (
      <span data-testid={`${testid}-compteur`} className="flex-shrink-0 font-data text-[11px] uppercase tracking-[0.08em] text-slate-500">
        {indication}
      </span>
    )}
    {valeur && (
      <button
        type="button"
        data-testid={`${testid}-effacer`}
        onClick={() => onChange('')}
        title="Effacer"
        className="flex-shrink-0 text-slate-400 transition-colors duration-150 hover:text-slate-900"
      >
        <X size={14} />
      </button>
    )}
  </div>
);

// Combobox de contacts : filtre instantané sur le nom, le téléphone, la matricule fiscale ou la CIN.
export const ContactCombobox = ({
  contacts,
  value,
  onChange,
  testid,
  placeholder = 'Rechercher : nom, téléphone, matricule fiscale…',
  detail,
  className = '',
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const box = useRef(null);

  const selection = contacts.find((c) => c.id === value);

  useEffect(() => {
    const clic = (e) => {
      if (box.current && !box.current.contains(e.target)) {
        setOuvert(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', clic);
    return () => document.removeEventListener('mousedown', clic);
  }, []);

  const resultats = useMemo(() => {
    const t = norm(q);
    if (!t) return contacts.slice(0, 50);
    return contacts.filter((c) => correspondContact(c, q)).slice(0, 50);
  }, [contacts, q]);

  const choisir = (c) => {
    onChange(c.id);
    setOuvert(false);
    setQ('');
  };

  const clavier = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, resultats.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (resultats[idx]) choisir(resultats[idx]);
    } else if (e.key === 'Escape') {
      setOuvert(false);
      setQ('');
    }
  };

  return (
    <div ref={box} className={`relative ${className}`}>
      <div className="mt-1.5 flex h-11 items-center gap-2 border border-slate-300 bg-slate-50 px-3 transition-colors duration-150 focus-within:border-slate-900 focus-within:bg-white">
        <Search size={14} className="flex-shrink-0 text-slate-400" />
        <input
          data-testid={testid}
          value={ouvert ? q : selection?.nom ?? ''}
          placeholder={selection ? selection.nom : placeholder}
          onFocus={() => {
            setOuvert(true);
            setIdx(0);
          }}
          onChange={(e) => {
            setQ(e.target.value);
            setOuvert(true);
            setIdx(0);
          }}
          onKeyDown={clavier}
          className="h-full w-full min-w-0 bg-transparent font-data text-sm text-slate-900 outline-none placeholder:text-slate-400"
        />
        {selection && (
          <button
            type="button"
            data-testid={`${testid}-effacer`}
            onClick={() => {
              onChange('');
              setQ('');
            }}
            className="flex-shrink-0 text-slate-400 transition-colors duration-150 hover:text-slate-900"
            title="Effacer"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {selection && !ouvert && (
        <p data-testid={`${testid}-detail`} className="mt-1.5 font-data text-[11px] text-slate-500">
          {detail ? detail(selection) : [selection.type, selection.tel, selection.mf && `MF ${selection.mf}`].filter(Boolean).join(' · ')}
        </p>
      )}

      {ouvert && (
        <ul
          data-testid={`${testid}-liste`}
          className="absolute z-[80] mt-1 max-h-72 w-full overflow-y-auto border border-slate-300 bg-white shadow-lg"
        >
          {resultats.length === 0 ? (
            <li data-testid={`${testid}-vide`} className="px-3 py-3 text-sm text-slate-500">
              Aucun contact ne correspond à « {q} »
            </li>
          ) : (
            resultats.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  data-testid={`${testid}-option-${c.id}`}
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => choisir(c)}
                  className={`flex w-full flex-col items-start px-3 py-2 text-left transition-colors duration-150 ${
                    i === idx ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'
                  }`}
                >
                  <span className="text-sm font-semibold">{c.nom}</span>
                  <span className={`font-data text-[11px] ${i === idx ? 'text-slate-300' : 'text-slate-500'}`}>
                    {detail
                      ? detail(c)
                      : [c.type, c.tel, c.mf && `MF ${c.mf}`, c.cin && `CIN ${c.cin}`].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
